import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  SquaresFour, ShieldCheck, Buildings, Coins, Users as UsersIcon,
  ListChecks, UserGear, FileText, SignOut, CaretUp, CaretDown, Lock, Package,
  CalendarCheck, CalendarBlank, List as ListIcon, X as XIcon, CheckSquare, Handshake,
  Notepad, FileText as ReportIcon, Money, Bank,
} from "@phosphor-icons/react";
import { useAuth, hasMinRole } from "@/contexts/AuthContext";
import {
  isExecutive, isFinance, isHR, isProcurement, isProjectManager,
  isCompliance, isAssetManager, isStaffOnly, isBranchUser, can,
} from "@/lib/roles";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import NotificationsBell from "@/components/app/NotificationsBell";
import GlobalSearch from "@/components/app/GlobalSearch";
import { roleLabel } from "@/lib/roles";

// Role-aware navigation, organised into collapsible groups. Each item declares
// which capability sees it via `visibleFor(user)` and which counter key (if
// any) drives its sidebar badge. Group `label: null` = always-visible top items.
const NAV_GROUPS = [
  {
    id: "home", label: null,
    items: [
      { to: "/",       label: "Dashboard",        icon: SquaresFour, end: true,
        visibleFor: (u) => !isStaffOnly(u) && !isBranchUser(u) },
      { to: "/branch", label: "Branch Dashboard", icon: SquaresFour,
        visibleFor: (u) => isBranchUser(u) },
      { to: "/me",     label: "My Workspace",     icon: UsersIcon,
        visibleFor: (u) => isStaffOnly(u) || ["finance_officer","hr_officer","procurement_officer"].includes(u?.role) },
    ],
  },
  {
    id: "work", label: "Work",
    items: [
      { to: "/approvals",   label: "Approvals",                    icon: CheckSquare, badge: "approvals_pending",
        visibleFor: (u) => can(u, "approvals.view") ?? ((isExecutive(u) || isFinance(u) || isHR(u) || isProcurement(u) || isProjectManager(u) || isAssetManager(u))
                           && u?.role !== "compliance_officer") },
      { to: "/tasks",       label: "Tasks",                        icon: ListChecks,  badge: "tasks_mine_pending",
        visibleFor: () => true },
      { to: "/compliance",  label: "Compliance",                   icon: ShieldCheck, badge: "compliance_pending",
        visibleFor: (u) => can(u, "compliance.view") ?? (isExecutive(u) || isCompliance(u)) },
      { to: "/projects",    label: "Projects",                     icon: Buildings,   badge: "projects_pending",
        visibleFor: (u) => can(u, "projects.view") ?? (isExecutive(u) || isProjectManager(u)) },
      { to: "/procurement", label: "Procurement",                  icon: Handshake,   badge: "procurement_pending",
        visibleFor: (u) => can(u, "procurement.view") ?? (isExecutive(u) || isProcurement(u)) },
      { to: "/ethics",      label: "Ethics & Grievance Reporting", icon: ReportIcon,  badge: "ethics_pending",
        visibleFor: () => true },
    ],
  },
  {
    id: "operations", label: "Operations",
    items: [
      { to: "/assets",       label: "Asset Management",  icon: Package, badge: "assets_pending",
        visibleFor: (u) => can(u, "assets.view") ?? (isExecutive(u) || isAssetManager(u) || isProjectManager(u)) },
      { to: "/my-assets",    label: "My Asset Requests", icon: Package, badge: "asset_requests_mine_pending",
        visibleFor: (u) => !!u && (isStaffOnly(u) || !(isExecutive(u) || isAssetManager(u))) },
      { to: "/appointments", label: "Appointments",      icon: CalendarCheck,
        visibleFor: (u) => !isStaffOnly(u) },
      { to: "/calendar",     label: "Calendar",          icon: CalendarBlank,
        visibleFor: () => true },
      { to: "/meetings",     label: "Meeting Minutes",   icon: Notepad,
        visibleFor: (u) => !isStaffOnly(u) },
    ],
  },
  {
    id: "finance", label: "Finance & Clients",
    items: [
      { to: "/finance",  label: "Finance",          icon: Coins, badge: "finance_pending",
        visibleFor: (u) => can(u, "finance.view") ?? (isExecutive(u) || isFinance(u)) },
      { to: "/payments", label: "Payment Requests", icon: Money,
        visibleFor: () => true },
      { to: "/funders",  label: "Funders",          icon: Bank,
        visibleFor: (u) => !isStaffOnly(u) && !isBranchUser(u) },
      { to: "/clients",  label: "Clients",          icon: Handshake,
        visibleFor: (u) => can(u, "clients.view") ?? (isExecutive(u) || isFinance(u) || isProjectManager(u) || isProcurement(u) || isCompliance(u)) },
    ],
  },
  {
    id: "people", label: "People",
    items: [
      { to: "/hr",         label: "Human Resources", icon: UsersIcon, badge: "hr_pending",
        visibleFor: (u) => can(u, "hr.view") ?? (isExecutive(u) || isHR(u)) },
      { to: "/my-profile", label: "My Profile",      icon: UsersIcon,
        visibleFor: (u) => !!u && (isStaffOnly(u) || !isHR(u)) },
    ],
  },
  {
    id: "admin", label: "Administration",
    items: [
      { to: "/users",       label: "User Management",   icon: UserGear,
        visibleFor: (u) => can(u, "users.view") ?? hasMinRole(u, "admin") },
      { to: "/permissions", label: "Permissions",       icon: Lock,
        visibleFor: (u) => u?.role === "super_admin" },
      { to: "/audit",       label: "Audit Log",         icon: FileText,
        visibleFor: (u) => can(u, "audit.view") ?? hasMinRole(u, "admin") },
      { to: "/test-data",   label: "Test Data Control", icon: Lock,
        visibleFor: (u) => u?.role === "super_admin" },
    ],
  },
];

// Returns groups with only the items visible to the current user (empty groups dropped).
function filterNavGroups(user) {
  if (!user) return [];
  return NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((n) => {
      try { return n.visibleFor(user); } catch { return false; }
    }),
  })).filter((g) => g.items.length > 0);
}

function navItemMatches(item, pathname) {
  return item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`) || (item.to !== "/" && pathname.startsWith(item.to));
}

function NavItemLink({ item, counters, onClose }) {
  const { to, label, icon: Icon, end, badge } = item;
  const count = badge ? (counters[badge] || 0) : 0;
  return (
    <NavLink
      key={to}
      to={to}
      end={end}
      onClick={onClose}
      className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Icon size={18} weight="regular" />
      <span className="flex-1">{label}</span>
      {badge && count > 0 && (
        <span
          className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 font-mono-alt text-[10px] font-semibold"
          style={{ background: "#1E5CBA", color: "#fff" }}
          data-testid={`badge-${badge}`}
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </NavLink>
  );
}

function ChangePasswordDialog({ trigger }) {
  const [open, setOpen] = useState(false);
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) return toast.error("Password must be at least 8 characters");
    if (newPw !== confirmPw) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Password changed successfully");
      setOpen(false);
      setOldPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Change password</DialogTitle>
          <DialogDescription>Minimum 8 characters.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label>Current password</Label>
            <Input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} required data-testid="current-password-input" />
          </div>
          <div>
            <Label>New password</Label>
            <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required data-testid="new-password-input" />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required data-testid="confirm-password-input" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="btn-primary-blue" data-testid="submit-change-password">
              {busy ? "Saving…" : "Change password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SidebarContent({ user, groups, onClose, counters = {}, openGroups, onToggleGroup }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  return (
    <>
      <div className="flex h-[72px] items-center px-5" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <img
          src="/descon-logo-white.png"
          alt="Descon Management System"
          data-testid="sidebar-logo"
          style={{ height: 42, width: "auto", objectFit: "contain" }}
        />
        {onClose && (
          <button onClick={onClose} className="ml-auto text-slate-300 hover:text-white md:hidden" aria-label="Close menu">
            <XIcon size={20} />
          </button>
        )}
      </div>
      <div className="px-5 pt-4 pb-3">
        <div className="font-mono-alt text-[10px] uppercase tracking-[0.3em]" style={{ color: "#7A8AAE" }}>
          Descon Management System
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-2">
        {groups.map((g) => {
          if (!g.label) {
            return g.items.map((item) => (
              <NavItemLink key={item.to} item={item} counters={counters} onClose={onClose} />
            ));
          }
          const open = !!openGroups[g.id];
          const rollup = g.items.reduce((s, it) => s + (it.badge ? (counters[it.badge] || 0) : 0), 0);
          return (
            <div key={g.id}>
              <button
                type="button"
                className="nav-group-header"
                onClick={() => onToggleGroup(g.id)}
                aria-expanded={open}
                data-testid={`nav-group-${g.id}`}
              >
                <span className="flex-1 text-left">{g.label}</span>
                {!open && rollup > 0 && (
                  <span
                    className="flex h-4 min-w-[18px] items-center justify-center rounded-full px-1 font-mono-alt text-[9px] font-semibold"
                    style={{ background: "#1E5CBA", color: "#fff" }}
                    data-testid={`nav-group-badge-${g.id}`}
                  >
                    {rollup > 99 ? "99+" : rollup}
                  </span>
                )}
                <CaretDown
                  size={11}
                  weight="bold"
                  style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.18s ease" }}
                />
              </button>
              {open && g.items.map((item) => (
                <NavItemLink key={item.to} item={item} counters={counters} onClose={onClose} />
              ))}
            </div>
          );
        })}
      </nav>

      {/* Bottom user block */}
      <div className="border-t px-3 py-3" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="group flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-white/5"
              data-testid="sidebar-user-menu"
            >
              <div
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full font-mono-alt text-xs font-semibold text-white"
                style={{ background: "var(--grad-blue)" }}
              >
                {(user?.name || user?.email || "U").slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-white" data-testid="sidebar-user-name">
                  {user?.name || "User"}
                </div>
                <div className="font-mono-alt text-[10px] uppercase tracking-widest" style={{ color: "#8997BC" }}>
                  {roleLabel(user?.role)}
                </div>
              </div>
              <CaretUp size={14} style={{ color: "#8997BC" }} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="w-60">
            <DropdownMenuLabel className="text-xs text-slate-500">{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <ChangePasswordDialog
              trigger={
                <DropdownMenuItem onSelect={(e) => e.preventDefault()} data-testid="menu-change-password">
                  <Lock size={14} className="mr-2" /> Change password
                </DropdownMenuItem>
              }
            />
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={async () => { await logout(); navigate("/"); }}
              data-testid="menu-logout"
            >
              <SignOut size={14} className="mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}

const NAV_OPEN_KEY = "descon.nav.open";

export function Layout({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [counters, setCounters] = useState({});
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem(NAV_OPEN_KEY)) || {}; } catch { return {}; }
  });
  const groups = filterNavGroups(user);

  const toggleGroup = (id) => setOpenGroups((prev) => {
    const next = { ...prev, [id]: !prev[id] };
    try { localStorage.setItem(NAV_OPEN_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  // Auto-open the group containing the current page.
  useEffect(() => {
    const g = NAV_GROUPS.find((grp) => grp.label && grp.items.some((i) => navItemMatches(i, location.pathname)));
    if (g) {
      setOpenGroups((prev) => {
        if (prev[g.id]) return prev;
        const next = { ...prev, [g.id]: true };
        try { localStorage.setItem(NAV_OPEN_KEY, JSON.stringify(next)); } catch { /* ignore */ }
        return next;
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!user) return;
    let active = true;
    const fetchCounters = async () => {
      try {
        const { data } = await api.get("/sidebar/counters");
        if (active) setCounters(data || {});
      } catch {
        /* silent — badges are cosmetic */
      }
    };
    fetchCounters();
    const id = setInterval(fetchCounters, 30000);
    return () => { active = false; clearInterval(id); };
  }, [user]);

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: "#F7F8FB" }}>
      {/* Desktop sidebar — locked to viewport, content area below scrolls */}
      <aside
        className="descon-sidebar hidden h-screen w-64 shrink-0 flex-col md:flex"
        data-testid="sidebar"
      >
        <SidebarContent user={user} groups={groups} counters={counters} openGroups={openGroups} onToggleGroup={toggleGroup} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-drawer-backdrop"
          />
          <aside
            className="descon-sidebar fixed inset-y-0 left-0 z-50 flex w-72 flex-col md:hidden"
            data-testid="mobile-sidebar"
          >
            <SidebarContent user={user} groups={groups} counters={counters} openGroups={openGroups} onToggleGroup={toggleGroup} onClose={() => setMobileOpen(false)} />
          </aside>
        </>
      )}

      <div className="flex h-screen min-w-0 flex-1 flex-col overflow-hidden">
        {/* Personal-workspace roles see no global search — they can only access
           their own assigned data, never cross-org records. */}
        {(() => {
          const hideGlobalSearch =
            isStaffOnly(user) || user?.role === "compliance_officer";
          return (
            <>
              {/* Desktop topbar — global search + notifications */}
              <header
                className="hidden md:flex h-14 items-center gap-3 border-b border-slate-200 bg-white px-6"
                data-testid="desktop-topbar"
              >
                {!hideGlobalSearch && <GlobalSearch />}
                <div className="ml-auto flex items-center gap-2">
                  <NotificationsBell />
                </div>
              </header>

              {/* Mobile top bar — hamburger + search + bell */}
              <header
                className="flex h-14 items-center justify-between gap-2 bg-white px-4 md:hidden"
                style={{ boxShadow: "0 1px 0 rgba(10,22,51,0.06)" }}
                data-testid="mobile-header"
              >
                <button
                  onClick={() => setMobileOpen(true)}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-700"
                  aria-label="Open menu"
                  data-testid="mobile-menu-button"
                >
                  <ListIcon size={18} />
                </button>
                <div className="flex flex-1 items-center justify-end gap-2">
                  {!hideGlobalSearch && <GlobalSearch />}
                  <NotificationsBell />
                </div>
              </header>
            </>
          );
        })()}

        <main className="flex-1 overflow-y-auto" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
